import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

/**
 * GDPR-style cookie / consent management.
 *
 * We store the user's decision in localStorage under `dt_consent_v1` so it
 * persists across pages and sessions. Shape:
 *   { necessary: true, analytics: bool, marketing: bool, timestamp: ISOString }
 *
 * `necessary` is always true (cookies required for auth/cart to function).
 * `analytics` gates GTAG.  `marketing` gates Meta Pixel + retargeting cookies.
 *
 * A `null` value means the user has not decided yet — the banner shows.
 */
const KEY = 'dt_consent_v1';
const ConsentContext = createContext(null);

const readStored = () => {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed !== 'object' || !parsed) return null;
    return {
      necessary: true,
      analytics: !!parsed.analytics,
      marketing: !!parsed.marketing,
      timestamp: parsed.timestamp || null,
    };
  } catch { return null; }
};

export const ConsentProvider = ({ children }) => {
  const [consent, setConsent] = useState(() => readStored());

  const save = useCallback((partial) => {
    const next = {
      necessary: true,
      analytics: false,
      marketing: false,
      ...(partial || {}),
      timestamp: new Date().toISOString(),
    };
    try { localStorage.setItem(KEY, JSON.stringify(next)); } catch {}
    setConsent(next);
    // Broadcast so <AnalyticsTags/> can react without a full reload.
    try { window.dispatchEvent(new CustomEvent('dt-consent-change', { detail: next })); } catch {}
    return next;
  }, []);

  const acceptAll = useCallback(() => save({ analytics: true, marketing: true }), [save]);
  const rejectAll = useCallback(() => save({ analytics: false, marketing: false }), [save]);
  const reset     = useCallback(() => { try { localStorage.removeItem(KEY); } catch {} setConsent(null); }, []);

  useEffect(() => {
    // If a previous version wrote a different key, migrate silently.
    if (consent === null) {
      const legacy = localStorage.getItem('cookieConsent');
      if (legacy === 'accepted') save({ analytics: true, marketing: true });
      else if (legacy === 'rejected') save({ analytics: false, marketing: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = {
    consent,
    decided: consent !== null,
    analytics: !!consent?.analytics,
    marketing: !!consent?.marketing,
    save,
    acceptAll,
    rejectAll,
    reset,
  };

  return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>;
};

export const useConsent = () => useContext(ConsentContext);
